import { ChevronRight, Megaphone } from 'lucide-react';
import { LINK_ENABLED } from '@/lib/features';
import type { NoticeState, Preview } from '@/lib/types';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

/**
 * I pezzi che shadcn non ha perché sono di questo prodotto: lo stato di un
 * avviso, l'anteprima della card in app, l'anteprima della notifica.
 * Tutto il resto viene da `components/ui/`, che sono i componenti shadcn
 * originali e non vanno modificati a mano.
 */

/* -------------------------------------------------------------------------- */
/* Stato                                                                      */
/* -------------------------------------------------------------------------- */

const STATES: Record<NoticeState, { label: string; className: string }> = {
  attivo: { label: 'In corso', className: 'bg-verde text-white' },
  programmato: { label: 'Programmato', className: 'bg-primary text-white' },
  scaduto: { label: 'Scaduto', className: 'bg-muted text-ink3 border-border' },
  ritirato: { label: 'Ritirato', className: 'bg-muted text-ink4 border-border' },
  bozza: { label: 'Bozza', className: 'bg-muted text-ink3 border-border' },
};

export function StateBadge({ state }: { state: NoticeState }) {
  const s = STATES[state];
  return <Badge className={s.className}>{s.label}</Badge>;
}

/* -------------------------------------------------------------------------- */
/* Anteprime                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * L'avviso come lo vedrà il cittadino.
 *
 * **Quattro elementi, perché quattro ne disegna l'app**: l'intestazione fissa
 * «Avviso dal Comune» col megafono, il titolo, il corpo. Nient'altro. Fino al
 * 14/08/2026 qui comparivano anche il nome dell'ente, «Valido fino a…» e la
 * firma «Protezione Civile comunale»: tre righe che in
 * `am-mobile/src/components/comune-notices.tsx` non esistono, e che quindi
 * l'operatore approvava senza che nessuno le leggesse mai. Chi cambia questo
 * componente apra quel file prima: l'unica cosa che deve somigliare a questa
 * anteprima è la card vera.
 *
 * Il nome del Comune non c'è perché nell'app è già il titolo della pagina su
 * cui la card sta. La data di pubblicazione, che nell'app c'è, compare solo
 * a box aperto — qui il box è chiuso, come lo trova il cittadino.
 *
 * Due regole non negoziabili, ereditate da docs/11 §6:
 *
 *  1. **Mai la palette dei livelli.** Niente verde/gialla/arancione/rossa, né
 *     la parola «Allerta». Una seconda scala sugli stessi quattro colori,
 *     decisa da un altro soggetto, rende illeggibili entrambe.
 *  2. **Il testo arriva da fuori** — anche qui, dove «fuori» è la textarea
 *     accanto. Si rende come testo, mai come HTML: nessun
 *     `dangerouslySetInnerHTML`, nessun link auto-rilevato.
 *
 * I contenuti arrivano già composti e ripuliti dal server (`preview.card`):
 * questo componente non compone niente, o l'anteprima somiglierebbe a ciò che
 * parte invece di esserlo.
 */
export function NoticePreview({ card }: { card: Preview['card'] }) {
  return (
    // Non è una `Card`: sta dentro la sezione della conferma, che a sua volta
    // lo è, e una card dentro una card raddoppia bordi e imbottitura senza
    // aggiungere una gerarchia che qualcuno possa leggere.
    <article className="rounded-lg border bg-card px-4 py-4">
      {/* L'intestazione dell'app è questa stringa fissa, non il nome dell'ente:
          chi legge è già dentro la scheda di quel Comune. */}
      <p className="flex items-center gap-1.5 text-[13px] font-semibold text-ink3">
        <Megaphone className="size-3.5" aria-hidden="true" />
        Avviso dal Comune
      </p>

      <h3 className="mt-2 text-[17px] font-semibold leading-snug text-ink">
        {card.title}
      </h3>

      {/* `whitespace-pre-line`: gli a capo dell'operatore si rispettano,
          come fa la colonna `body` sul server. Nient'altro è interpretato. */}
      <p className="mt-1.5 whitespace-pre-line text-[15px] leading-relaxed text-ink2">
        {card.body}
      </p>

      {LINK_ENABLED && card.link_url && (
        <p className="mt-3 flex items-center gap-0.5 text-[15px] font-medium text-primary">
          {card.link_label}
          <ChevronRight className="size-4" aria-hidden="true" />
        </p>
      )}
    </article>
  );
}

/**
 * La notifica come apparirà sulla schermata di blocco.
 *
 * Glifo diverso dal bollettino e nome del Comune per primo: una push comunale
 * non deve MAI avere la forma di «⚠️ Allerta {colore} — {zona}». Chi la riceve
 * deve capire in mezzo secondo che a parlare è il municipio, non la Regione.
 */
export function PushPreview({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl bg-ink px-4 py-3.5">
      <p className="text-[13px] font-medium text-white/60">{title}</p>
      <p className="mt-0.5 text-[15px] font-medium leading-snug text-white">{body}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Misure                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Un contatore con la sua barra.
 *
 * La barra diventa rossa quando il budget è finito, non quando «sta per»:
 * un colore d'allarme che arriva presto è un colore che si impara a ignorare.
 */
export function Meter({
  label,
  used,
  cap,
}: {
  label: string;
  used: number;
  cap: number;
}) {
  const pct = cap > 0 ? Math.min(100, (used / cap) * 100) : 0;
  const done = used >= cap;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[13px] font-medium text-ink3">{label}</span>
        <span className={cn('tnum text-[15px] font-semibold', done ? 'text-rossa' : 'text-ink')}>
          {used}/{cap}
        </span>
      </div>
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full transition-all', done ? 'bg-rossa' : 'bg-primary')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** Un dato secco: numero grande, etichetta sotto. */
export function Stat({
  value,
  label,
  tone = 'default',
}: {
  value: string;
  label: string;
  tone?: 'default' | 'danger';
}) {
  return (
    <div>
      <p
        className={cn(
          'tnum text-[28px] font-bold leading-none',
          tone === 'danger' ? 'text-rossa' : 'text-ink',
        )}
      >
        {value}
      </p>
      <p className="mt-1.5 text-[13px] text-ink3">{label}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Stati vuoti                                                                */
/* -------------------------------------------------------------------------- */

export function Loading({ what = 'Carico' }: { what?: string }) {
  return (
    <p className="py-14 text-center text-[15px] text-ink4" role="status">
      {what}…
    </p>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <Card className="border-dashed shadow-none">
      <CardContent className="py-10 text-center text-[15px] text-ink3">
        {children}
      </CardContent>
    </Card>
  );
}

/**
 * Titolo di schermata.
 *
 * `above` porta navigazione e stato — il ritorno indietro, le etichette di
 * stato di un avviso. Non un occhiello: un'etichetta che si limita a ripetere
 * la categoria di ciò che sta sotto è peso morto, e il titolo si regge da sé.
 */
export function PageTitle({
  children,
  above,
}: {
  children: ReactNode;
  above?: ReactNode;
}) {
  return (
    <header className="mb-6">
      {above && <div className="mb-1">{above}</div>}
      <h1 className="text-[30px] font-bold leading-tight tracking-[-0.02em] text-ink">
        {children}
      </h1>
    </header>
  );
}
